function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { combineTransactionSteps, getChangedRanges, findChildrenInRange, getMarksBetween, getAttributes, Mark, mergeAttributes } from '@tiptap/core';
import { find, registerCustomProtocol, reset } from 'linkifyjs';
import { Plugin, PluginKey } from '@tiptap/pm/state';
function autolink(options) {
  return new Plugin({
    key: new PluginKey('autolink'),
    appendTransaction: (transactions, oldState, newState) => {
      const docChanges = transactions.some(transaction => transaction.docChanged) && !oldState.doc.eq(newState.doc);
      const preventAutolink = transactions.some(transaction => transaction.getMeta('preventAutolink'));
      if (!docChanges || preventAutolink) {
        return;
      }
      const tr = newState.tr;
      const transform = combineTransactionSteps(oldState.doc, [...transactions]);
      const changes = getChangedRanges(transform);
      changes.forEach(_ref => {
        let newRange = _ref.newRange;
        // Now let’s see if we can add new links.
        const nodesInChangedRanges = findChildrenInRange(newState.doc, newRange, node => node.isTextblock);
        let textBlock;
        let textBeforeWhitespace;
        if (nodesInChangedRanges.length > 1) {
          // Grab the first node within the changed ranges (ex. the first of two paragraphs when hitting enter).
          textBlock = nodesInChangedRanges[0];
          textBeforeWhitespace = newState.doc.textBetween(textBlock.pos, textBlock.pos + textBlock.node.nodeSize, undefined, ' ');
        } else if (nodesInChangedRanges.length
        // We want to make sure to include the block seperator argument to treat hard breaks like spaces.
        && newState.doc.textBetween(newRange.from, newRange.to, ' ', ' ').endsWith(' ')) {
          textBlock = nodesInChangedRanges[0];
          textBeforeWhitespace = newState.doc.textBetween(textBlock.pos, newRange.to, undefined, ' ');
        }
        if (textBlock && textBeforeWhitespace) {
          const wordsBeforeWhitespace = textBeforeWhitespace.split(' ').filter(s => s !== '');
          if (wordsBeforeWhitespace.length <= 0) {
            return false;
          }
          const lastWordBeforeSpace = wordsBeforeWhitespace[wordsBeforeWhitespace.length - 1];
          const lastWordAndBlockOffset = textBlock.pos + textBeforeWhitespace.lastIndexOf(lastWordBeforeSpace);
          if (!lastWordBeforeSpace) {
            return false;
          }
          find(lastWordBeforeSpace).filter(link => link.isLink)
          // Calculate link position.
          .map(link => _objectSpread(_objectSpread({}, link), {}, {
            from: lastWordAndBlockOffset + link.start + 1,
            to: lastWordAndBlockOffset + link.end + 1
          }))
          // ignore link inside code mark
          .filter(link => {
            if (!newState.schema.marks.code) {
              return true;
            }
            return !newState.doc.rangeHasMark(link.from, link.to, newState.schema.marks.code);
          })
          // validate link
          .filter(link => {
            if (options.validate) {
              return options.validate(link.value);
            }
            return true;
          })
          // Add link mark.
          .forEach(link => {
            if (getMarksBetween(link.from, link.to, newState.doc).some(item => item.mark.type === options.type)) {
              return;
            }
            tr.addMark(link.from, link.to, options.type.create({
              href: link.href
            }));
          });
        }
      });
      if (!tr.steps.length) {
        return;
      }
      return tr;
    }
  });
}
function clickHandler(options) {
  return new Plugin({
    key: new PluginKey('handleClickLink'),
    props: {
      handleClick: (view, pos, event) => {
        var _a, _b;
        if (event.button !== 0) {
          return false;
        }
        const eventTarget = event.target;
        if (eventTarget.nodeName !== 'A') {
          return false;
        }
        const attrs = getAttributes(view.state, options.type.name);
        const link = event.target;
        const href = (_a = link === null || link === void 0 ? void 0 : link.href) !== null && _a !== void 0 ? _a : attrs.href;
        const target = (_b = link === null || link === void 0 ? void 0 : link.target) !== null && _b !== void 0 ? _b : attrs.target;
        if (link && href) {
          if (view.editable) {
            window.open(href, target);
          }
          return true;
        }
        return false;
      }
    }
  });
}
function pasteHandler(options) {
  return new Plugin({
    key: new PluginKey('handlePasteLink'),
    props: {
      handlePaste: (view, event, slice) => {
        var _a, _b;
        const state = view.state;
        const selection = state.selection;
        // Do not proceed if in code block.
        if (state.doc.resolve(selection.from).parent.type.spec.code) {
          return false;
        }
        let textContent = '';
        slice.content.forEach(node => {
          textContent += node.textContent;
        });
        let isAlreadyLink = false;
        slice.content.descendants(node => {
          if (node.marks.some(mark => mark.type.name === options.type.name)) {
            isAlreadyLink = true;
          }
        });
        if (isAlreadyLink) {
          return;
        }
        const link = find(textContent).find(item => item.isLink && item.value === textContent);
        if (!selection.empty && options.linkOnPaste) {
          const pastedLink = (link === null || link === void 0 ? void 0 : link.href) || null;
          if (pastedLink) {
            options.editor.commands.setMark(options.type, {
              href: pastedLink
            });
            return true;
          }
        }
        const firstChildIsText = ((_a = slice.content.firstChild) === null || _a === void 0 ? void 0 : _a.type.name) === 'text';
        const firstChildContainsLinkMark = (_b = slice.content.firstChild) === null || _b === void 0 ? void 0 : _b.marks.some(mark => mark.type.name === options.type.name);
        if (firstChildIsText && firstChildContainsLinkMark || !options.linkOnPaste) {
          return false;
        }
        if (link && selection.empty) {
          options.editor.commands.insertContent(`<a href="${link.href}">${link.href}</a>`);
          return true;
        }
        const tr = state.tr;
        let deleteOnly = false;
        if (!selection.empty) {
          deleteOnly = true;
          tr.delete(selection.from, selection.to);
        }
        let currentPos = selection.from;
        let fragmentLinks = [];
        slice.content.forEach(node => {
          fragmentLinks = find(node.textContent);
          tr.insert(currentPos - 1, node);
          if (fragmentLinks.length > 0) {
            deleteOnly = false;
            fragmentLinks.forEach(fragmentLink => {
              const linkStart = currentPos + fragmentLink.start;
              const linkEnd = currentPos + fragmentLink.end;
              const hasMark = tr.doc.rangeHasMark(linkStart, linkEnd, options.type);
              if (!hasMark) {
                tr.addMark(linkStart, linkEnd, options.type.create({
                  href: fragmentLink.href
                }));
              }
            });
          }
          currentPos += node.nodeSize;
        });
        const hasFragmentLinks = fragmentLinks.length > 0;
        if (tr.docChanged && !deleteOnly && hasFragmentLinks) {
          options.editor.view.dispatch(tr);
          return true;
        }
        return false;
      }
    }
  });
}
const Link = Mark.create({
  name: 'link',
  priority: 1000,
  keepOnSplit: false,
  onCreate() {
    this.options.protocols.forEach(protocol => {
      if (typeof protocol === 'string') {
        registerCustomProtocol(protocol);
        return;
      }
      registerCustomProtocol(protocol.scheme, protocol.optionalSlashes);
    });
  },
  onDestroy() {
    reset();
  },
  inclusive() {
    return this.options.autolink;
  },
  addOptions() {
    return {
      openOnClick: true,
      linkOnPaste: true,
      autolink: true,
      protocols: [],
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
        class: null
      },
      validate: undefined
    };
  },
  addAttributes() {
    return {
      href: {
        default: null
      },
      target: {
        default: this.options.HTMLAttributes.target
      },
      rel: {
        default: this.options.HTMLAttributes.rel
      },
      class: {
        default: this.options.HTMLAttributes.class
      }
    };
  },
  parseHTML() {
    return [{
      tag: 'a[href]:not([href *= "javascript:" i])'
    }];
  },
  renderHTML(_ref2) {
    let HTMLAttributes = _ref2.HTMLAttributes;
    return ['a', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
  addCommands() {
    return {
      setLink: attributes => _ref3 => {
        let chain = _ref3.chain;
        return chain().setMark(this.name, attributes).setMeta('preventAutolink', true).run();
      },
      toggleLink: attributes => _ref4 => {
        let chain = _ref4.chain;
        return chain().toggleMark(this.name, attributes, {
          extendEmptyMarkRange: true
        }).setMeta('preventAutolink', true).run();
      },
      unsetLink: () => _ref5 => {
        let chain = _ref5.chain;
        return chain().unsetMark(this.name, {
          extendEmptyMarkRange: true
        }).setMeta('preventAutolink', true).run();
      }
    };
  },
  addProseMirrorPlugins() {
    const plugins = [];
    if (this.options.autolink) {
      plugins.push(autolink({
        type: this.type,
        validate: this.options.validate
      }));
    }
    if (this.options.openOnClick) {
      plugins.push(clickHandler({
        type: this.type
      }));
    }
    plugins.push(pasteHandler({
      editor: this.editor,
      type: this.type,
      linkOnPaste: this.options.linkOnPaste
    }));
    return plugins;
  }
});
export { Link, Link as default };